/**
 * Table Column Configuration for Quotation & Sales Order PDFs
 * Defines all columns, mandatory/optional status, weights, and dynamic scaling rules.
 */

// All possible columns for the line items table
// Weight = relative width allocation. Rebalanced (2026-05) to prevent
// Tool Cost / Sample Cost currency values from overflowing into adjacent cells.
export const ALL_COLUMNS = [
  { id: 'row_num',        label: '#',                 mandatory: true,  align: 'center', weight: 0.5 },
  { id: 'part_no',        label: 'Part No.',          mandatory: true,  align: 'left',   weight: 1.0 },
  { id: 'description',    label: 'Description',       mandatory: true,  align: 'left',   weight: 2.6 },
  { id: 'hs_code',        label: 'HS Code',           mandatory: false, align: 'left',   weight: 0.9 },
  { id: 'net_weight',     label: 'Net Wt (g)',        mandatory: false, align: 'right',  weight: 0.9 },
  { id: 'total_weight',   label: 'Total Wt (Kg)',     mandatory: false, align: 'right',  weight: 1.0 },
  { id: 'qty',            label: 'Qty',               mandatory: true,  align: 'right',  weight: 0.8 },
  { id: 'uom',            label: 'UOM',               mandatory: true,  align: 'center', weight: 0.6 },
  { id: 'packing_qty',    label: 'Pack Qty (Pcs/Box)', mandatory: false, align: 'right', weight: 0.8, docTypes: ['order'] },
  { id: 'box_size',       label: 'Box Size',          mandatory: false, align: 'left',   weight: 1.0, docTypes: ['order'] },
  { id: 'ship_date',      label: 'Ship Date',         mandatory: false, align: 'center', weight: 1.0, docTypes: ['order'] },
  { id: 'remarks',        label: 'Remarks',           mandatory: false, align: 'left',   weight: 1.6, docTypes: ['order'] },
  { id: 'tool_cost',      label: 'Tool Cost',         mandatory: false, align: 'right',  weight: 1.1 },
  { id: 'sample_cost',    label: 'Sample Cost',       mandatory: false, align: 'right',  weight: 1.1 },
  { id: 'sample_lt',      label: 'Sample LT (Wks)',   mandatory: false, align: 'center', weight: 0.7 },
  { id: 'mfg_lt',         label: 'Mfg LT (Wks)',      mandatory: false, align: 'center', weight: 0.7 },
  { id: 'price',          label: 'Price',             mandatory: true,  align: 'right',  weight: 1.3 },
  // "Per" is merged into Price — no separate column
  { id: 'total',          label: 'Total',             mandatory: true,  align: 'right',  weight: 1.3 },
];

export const MANDATORY_IDS = ALL_COLUMNS.filter(c => c.mandatory).map(c => c.id);
export const OPTIONAL_IDS  = ALL_COLUMNS.filter(c => !c.mandatory).map(c => c.id);

// Wire-specific column set (used by Wire Quotation PDFs).
// Wire pricing is per kg/MT, no tooling/sample, and includes coil info.
export const WIRE_COLUMNS = [
  { id: 'row_num',        label: '#',                mandatory: true,  align: 'center', weight: 0.4 },
  { id: 'part_no',        label: 'Part No.',         mandatory: false, align: 'left',   weight: 1.0 },
  { id: 'description',    label: 'Description',      mandatory: true,  align: 'left',   weight: 2.4 },
  { id: 'wire_diameter',  label: 'Diameter (mm)',    mandatory: true,  align: 'center', weight: 1.0 },
  { id: 'wire_grade',     label: 'Grade',            mandatory: true,  align: 'center', weight: 0.9 },
  { id: 'uts_notes',      label: 'UTS / Hardness',   mandatory: false, align: 'left',   weight: 1.2 },
  { id: 'hs_code',        label: 'HS Code',          mandatory: false, align: 'left',   weight: 0.8 },
  { id: 'coil_weight',    label: 'Coil Wt (Kg)',     mandatory: false, align: 'right',  weight: 0.9 },
  { id: 'no_of_coils',    label: 'Coils',            mandatory: false, align: 'right',  weight: 0.6 },
  { id: 'total_kg',       label: 'Total Wt (Kg)',    mandatory: true,  align: 'right',  weight: 1.0 },
  { id: 'price',          label: 'Price',            mandatory: true,  align: 'right',  weight: 1.3 },
  { id: 'total',          label: 'Total',            mandatory: true,  align: 'right',  weight: 1.3 },
];

export const WIRE_MANDATORY_IDS = WIRE_COLUMNS.filter(c => c.mandatory).map(c => c.id);
export const WIRE_OPTIONAL_IDS  = WIRE_COLUMNS.filter(c => !c.mandatory).map(c => c.id);

// Default column profiles per document type
export const DEFAULT_PROFILES = {
  quotation: ['row_num', 'part_no', 'description', 'hs_code', 'qty', 'uom', 'mfg_lt', 'price', 'total'],
  order:     ['row_num', 'part_no', 'description', 'hs_code', 'net_weight', 'total_weight', 'qty', 'uom', 'ship_date', 'price', 'total'],
  wire_quotation: ['row_num', 'part_no', 'description', 'wire_diameter', 'wire_grade', 'uts_notes', 'hs_code', 'coil_weight', 'no_of_coils', 'total_kg', 'price', 'total'],
};

/**
 * Given items array and selected column IDs, filter out optional columns
 * where ALL items have empty/null/zero/dash values.
 */
export function autoHideEmptyColumns(items, selectedIds) {
  if (!items?.length) return selectedIds;

  const optionalVisible = selectedIds.filter(id => OPTIONAL_IDS.includes(id));

  const nonEmptyOptional = optionalVisible.filter(colId => {
    return items.some(item => {
      const val = getItemValue(item, colId);
      return val !== null && val !== undefined && val !== '' && val !== '-' && val !== 0;
    });
  });

  // Return mandatory + non-empty optional, preserving original order
  return selectedIds.filter(id => MANDATORY_IDS.includes(id) || nonEmptyOptional.includes(id));
}

/**
 * Extract the raw value for a column from a line item object.
 */
export function getItemValue(item, colId) {
  switch (colId) {
    case 'hs_code':       return item.hs_code || item.live_part?.hs_code || null;
    case 'net_weight':    return item.net_weight_grams || item.live_part?.net_weight_grams || 0;
    case 'total_weight': {
      const nw = item.net_weight_grams || item.live_part?.net_weight_grams || 0;
      const qty = item.quantity || 0;
      return nw && qty ? (nw * qty) / 1000 : 0;
    }
    case 'tool_cost':     return item.tooling_cost || 0;
    case 'sample_cost':   return item.sample_cost || 0;
    case 'packing_qty':   return item.packing_qty || item.live_part?.packing_qty || 0;
    case 'box_size':      return item.box_size || item.live_part?.box_size || null;
    case 'ship_date':     return item.shipment_date || null;
    case 'remarks':       return item.remarks || null;
    case 'sample_lt':     return item.sample_lead_time || null;
    case 'mfg_lt':        return item.production_lead_time || item.manufacturing_lead_time || null;
    // Wire-specific
    case 'wire_diameter': {
      const a = item.wire_diameter_min_mm, b = item.wire_diameter_max_mm;
      if (!a && !b) return null;
      if (a && b && a !== b) return `${a} – ${b}`;
      return String(a || b);
    }
    case 'wire_grade':    return item.wire_grade || null;
    case 'uts_notes':     return item.uts_notes || null;
    case 'coil_weight':   return item.coil_weight_kg || 0;
    case 'no_of_coils':   return item.no_of_coils || 0;
    case 'total_kg':      return item.total_kg || item.quantity || 0;
    default:              return null;
  }
}

/**
 * Font scaling based on visible column count.
 */
export function getFontConfig(visibleCount) {
  if (visibleCount <= 7)  return { body: '11pt', header: '10pt',  padding: '8px 4px' };
  if (visibleCount <= 9)  return { body: '10pt', header: '9.5pt', padding: '7px 4px' };
  if (visibleCount <= 11) return { body: '9.5pt', header: '9pt',  padding: '6px 3px' };
  if (visibleCount <= 13) return { body: '9pt',   header: '8.5pt', padding: '5px 3px' };
  return                         { body: '8pt',   header: '7.5pt', padding: '4px 2px' };
}

/**
 * Calculate percentage widths for visible columns based on weights.
 * Pass `colSet` (default ALL_COLUMNS) to use a different column set (e.g., WIRE_COLUMNS).
 */
export function getColumnWidths(visibleIds, colSet = ALL_COLUMNS) {
  const cols = colSet.filter(c => visibleIds.includes(c.id));
  const totalWeight = cols.reduce((sum, c) => sum + c.weight, 0);
  const widths = {};
  cols.forEach(c => {
    widths[c.id] = `${((c.weight / totalWeight) * 100).toFixed(1)}%`;
  });
  return widths;
}

/**
 * Get the full ordered list of visible column definitions.
 */
export function getVisibleColumns(visibleIds, colSet = ALL_COLUMNS) {
  return colSet.filter(c => visibleIds.includes(c.id));
}

/**
 * Wire-aware autoHide that uses WIRE_OPTIONAL_IDS instead of OPTIONAL_IDS.
 */
export function autoHideEmptyWireColumns(items, selectedIds) {
  if (!items?.length) return selectedIds;
  const optionalVisible = selectedIds.filter(id => WIRE_OPTIONAL_IDS.includes(id));
  const nonEmpty = optionalVisible.filter(colId => items.some(item => {
    const val = getItemValue(item, colId);
    return val !== null && val !== undefined && val !== '' && val !== '-' && val !== 0;
  }));
  return selectedIds.filter(id => WIRE_MANDATORY_IDS.includes(id) || nonEmpty.includes(id));
}
